import { FC } from 'react';
import { Box, Paper, Skeleton, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

/**
 * Loading placeholders for the remediation screens.
 *
 * Each mirrors the layout it stands in for closely enough that the page does
 * not reflow when real data lands — a centred spinner collapses the page to
 * nothing and then snaps it back, which reads as two loads rather than one.
 *
 * The list screen is not here: it goes through `DataTable`, which builds its own
 * skeleton from the column definitions.
 */

/** Card shell shared by the scope sections and the run's document list. */
const SkeletonCard: FC<{ children: React.ReactNode }> = observer(({ children }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      {children}
    </Paper>
  );
});

/** Header strip: icon tile, title, subtitle — the scope sections' top band. */
const SkeletonCardHeader: FC = observer(() => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        p: 2.25,
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
      }}
    >
      <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2.5 }} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width={190} height={20} />
        <Skeleton variant="text" width="55%" height={14} />
      </Box>
      <Skeleton variant="text" width={70} height={16} />
    </Box>
  );
});

/** One selectable document row: checkbox, file tile, name, meta. */
const SkeletonDocumentRow: FC<{ last?: boolean }> = observer(({ last }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.75,
        px: 2.25,
        py: 1.5,
        borderBottom: last ? 'none' : `1px solid ${theme.palette.grey[300]}`,
      }}
    >
      <Skeleton variant="rounded" width={18} height={18} sx={{ borderRadius: 0.5 }} />
      <Skeleton variant="rounded" width={26} height={32} sx={{ borderRadius: 1.5 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="45%" height={16} />
        <Skeleton variant="text" width={60} height={12} />
      </Box>
      <Skeleton variant="rounded" width={110} height={8} sx={{ borderRadius: 999 }} />
      <Skeleton variant="rounded" width={90} height={22} sx={{ borderRadius: 999 }} />
    </Box>
  );
});

/**
 * Screen 3 — document selection. Two section cards of selectable rows, which is
 * what the scope endpoint always resolves to.
 */
export const RemediationScopeSkeleton: FC<{ rowsPerSection?: number }> = observer(
  ({ rowsPerSection = 3 }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {[0, 1].map((section) => (
        <SkeletonCard key={section}>
          <SkeletonCardHeader />
          {Array.from({ length: rowsPerSection }).map((_, i) => (
            <SkeletonDocumentRow key={i} last={i === rowsPerSection - 1} />
          ))}
        </SkeletonCard>
      ))}
    </Box>
  ),
);

/**
 * Screen 5 — live run. Hero band, the four count tiles, then the document list
 * with its pipeline bars.
 */
export const RemediationRunSkeleton: FC<{ rows?: number }> = observer(({ rows = 4 }) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Hero */}
      <Box
        sx={{
          px: 4,
          py: 3.5,
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2.25,
        }}
      >
        <Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: 4 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={260} height={28} />
          <Skeleton variant="text" width="50%" height={16} />
        </Box>
        <Skeleton variant="rounded" width={170} height={56} sx={{ borderRadius: 3 }} />
      </Box>

      <Box sx={{ px: { xs: 2, md: 5 }, py: 3, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        {/* Count tiles */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={74}
              sx={{ flex: 1, minWidth: 110, borderRadius: 3.5 }}
            />
          ))}
        </Box>

        {/* Document rows */}
        <SkeletonCard>
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              backgroundColor: theme.palette.grey[200],
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
            }}
          >
            <Skeleton variant="text" width={120} height={14} />
          </Box>
          {Array.from({ length: rows }).map((_, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2.25,
                px: 2.5,
                py: 2,
                borderBottom: i === rows - 1 ? 'none' : `1px solid ${theme.palette.grey[300]}`,
              }}
            >
              <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: 2 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="text" width="40%" height={16} />
                <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: 999 }} />
              </Box>
              {/* The five-segment pipeline bar. */}
              <Box sx={{ display: 'flex', gap: 0.75, flex: 1, maxWidth: 260 }}>
                {[0, 1, 2, 3, 4].map((seg) => (
                  <Skeleton
                    key={seg}
                    variant="rounded"
                    height={5}
                    sx={{ flex: 1, borderRadius: 999 }}
                  />
                ))}
              </Box>
              <Skeleton variant="rounded" width={120} height={32} sx={{ borderRadius: 2 }} />
            </Box>
          ))}
        </SkeletonCard>
      </Box>
    </Box>
  );
});

/**
 * Screen 6 — redline review. Top bar, the two document panes, and the change
 * rail. The rail is hidden below `lg`, matching the real layout.
 */
export const RemediationReviewSkeleton: FC = observer(() => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Top bar */}
      <Box
        sx={{
          px: 2.75,
          py: 1.75,
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={240} height={22} />
          <Skeleton variant="text" width={150} height={14} />
        </Box>
        <Skeleton variant="rounded" width={190} height={34} sx={{ borderRadius: 2.5 }} />
      </Box>

      {/* Panes + rail */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {[0, 1].map((pane) => (
          <Box
            key={pane}
            sx={{
              flex: 1,
              p: 3,
              minWidth: 0,
              borderInlineEnd:
                pane === 0 ? `1px solid ${theme.palette.grey[300]}` : undefined,
            }}
          >
            <Skeleton variant="text" width={90} height={12} sx={{ mb: 2 }} />
            {[95, 88, 92, 70, 90, 84, 60].map((w, i) => (
              <Skeleton key={i} variant="text" width={`${w}%`} height={14} />
            ))}
          </Box>
        ))}

        <Box
          sx={{
            width: 380,
            flexShrink: 0,
            p: 2,
            display: { xs: 'none', lg: 'block' },
            borderInlineStart: `1px solid ${theme.palette.grey[300]}`,
            backgroundColor: theme.palette.grey[200],
          }}
        >
          <Skeleton variant="rounded" height={220} sx={{ borderRadius: 4, mb: 2 }} />
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 2, mb: 1 }} />
          ))}
        </Box>
      </Box>
    </Box>
  );
});
